// ============================================================================
// MIRO TEMPLATE ADMIN PAGE
// Admin page for configuring the Miro template board and viewing discovered frames
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader, ExternalLink, Layout, Check } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '../components/EmptyState';
import {
  getTemplateBoardConfig,
  listTemplateFrames,
  scanTemplateBoard,
  MiroTemplateBoardConfig,
  MiroTemplateFrame
} from '../services/miro';

export default function MiroTemplateAdmin() {
  const [config, setConfig] = useState<MiroTemplateBoardConfig | null>(null);
  const [frames, setFrames] = useState<MiroTemplateFrame[]>([]);
  const [boardIdInput, setBoardIdInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [configResult, framesResult] = await Promise.all([
        getTemplateBoardConfig(),
        listTemplateFrames()
      ]);
      setConfig(configResult);
      setFrames(framesResult);
      if (configResult?.templateBoardId) {
        setBoardIdInput(configResult.templateBoardId);
      }
    } catch (err: any) {
      console.error('Failed to load Miro template config:', err);
      toast.error('Failed to load Miro template configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleScan() {
    if (!boardIdInput.trim()) {
      setError('Please enter a Miro board ID');
      return;
    }

    try {
      setScanning(true);
      setError(null);
      setSuccess(null);
      await scanTemplateBoard(boardIdInput.trim());
      setSuccess('Scan triggered. Discovering frames...');

      // Poll for results
      const pollInterval = setInterval(async () => {
        try {
          const newFrames = await listTemplateFrames();
          const newConfig = await getTemplateBoardConfig();
          if (newConfig?.lastScannedAt) {
            setFrames(newFrames);
            setConfig(newConfig);
            setScanning(false);
            setSuccess(`Found ${newFrames.length} template frame${newFrames.length !== 1 ? 's' : ''}`);
            clearInterval(pollInterval);
          }
        } catch { /* keep polling */ }
      }, 2000);

      // Stop polling after 30s
      setTimeout(() => {
        clearInterval(pollInterval);
        setScanning(false);
      }, 30000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
      setScanning(false);
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500">
        <Loader className="animate-spin mr-2" size={20} />
        Loading...
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
          <Layout size={24} className="text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Miro Templates</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Configure a Miro template board. Frames become selectable templates when users create project boards.
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-4">
          {error}
          <button onClick={() => setError(null)} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}
      {success && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-3 text-sm text-green-700 mb-4">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Template Board Config */}
      <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5 mb-6 space-y-4">
        <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Template Board</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={boardIdInput}
            onChange={e => setBoardIdInput(e.target.value)}
            placeholder="Enter Miro board ID (e.g. uXjVK...)"
            className="flex-1 rounded-xl border border-[#e2e8f0] px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30"
          />
          <button
            onClick={handleScan}
            disabled={scanning}
            className="inline-flex items-center gap-2 bg-primary text-navy font-semibold rounded-xl px-5 py-2.5 text-sm shadow-md shadow-primary/20 hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {scanning ? (
              <><Loader className="animate-spin" size={16} /> Scanning...</>
            ) : (
              <><RefreshCw size={16} /> {config?.lastScannedAt ? 'Re-scan' : 'Scan Board'}</>
            )}
          </button>
        </div>

        {config?.lastScannedAt && (
          <div className="text-xs text-slate-500">
            Last scanned: {new Date(config.lastScannedAt).toLocaleString()} | Board ID: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">{config.templateBoardId}</code>
          </div>
        )}
      </div>

      {/* Discovered Frames */}
      <div className="rounded-2xl border border-[#e2e8f0] bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Template Frames</h2>
          <span className="text-xs text-slate-400">{frames.length} frame{frames.length !== 1 ? 's' : ''}</span>
        </div>

        {frames.length === 0 ? (
          <EmptyState
            icon={Layout}
            title="No frames discovered"
            description={
              config?.templateBoardId
                ? 'No frames found. Make sure the template board has top-level frames, then scan again.'
                : 'Enter a template board ID above and click Scan to discover frames.'
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {frames.map(frame => (
              <div key={frame.frameId} className="rounded-2xl border border-[#e2e8f0] p-4 hover:border-primary/30 hover:shadow-sm transition-all">
                <div className="bg-primary/5 rounded-xl h-20 flex items-center justify-center mb-3">
                  <Layout size={28} className="text-primary/40" />
                </div>
                <div className="font-semibold text-slate-900 text-sm">{frame.title}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="text-xs text-slate-400">
                    {frame.itemCount} item{frame.itemCount !== 1 ? 's' : ''}
                    {' · '}
                    {Math.round(frame.geometry.width)}x{Math.round(frame.geometry.height)}
                  </div>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-200">
                    <Check size={10} />
                    Available
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {config?.templateBoardId && (
          <a
            href={`https://miro.com/app/board/${config.templateBoardId}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-dark font-medium"
          >
            Open template board in Miro <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
}
